import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProgressBar } from "react-bootstrap";
import {
  Box,
  Breadcrumbs,
  Grid2,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import Icon from "../Icon/Icon";
import { transformDayJs } from "../../helpers/dateTransform";
import { useNavigate } from "react-router";

export const ReportHeader = ({
  reportDetail,
  icon = "outline_document_text",
  edit = false,
  title = "",
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [t] = useTranslation("report");

  const breadcrumbItems = [
    { label: t("ReportViewer.home"), route: "/", iconName: "bold_home" },
    { label: t("ReportViewer.all_reports"), route: "/reports/all" },
    { label: `${reportDetail.folio || t("ReportViewer.no_folio")}` },
  ];

  const difference = useMemo(() => {
    if (!reportDetail?.created_at || !reportDetail?.status?.assigned)
      return null;
    const start = transformDayJs(reportDetail.created_at);
    const end = transformDayJs(reportDetail.status.assigned);
    return Math.abs(start.diff(end, "day"));
  }, [reportDetail]);

  const handleClick = useCallback(
    (event, route) => {
      event.preventDefault();
      if (route) navigate(route);
    },
    [navigate],
  );

  return (
    <Grid2
      container
      className="rounded"
      sx={{
        background: `linear-gradient(186deg, ${theme.palette.common.white} 0%, ${theme.palette.primary.main} 520%)`,
        border: `1px solid ${theme.palette.primary.main}`,
        px: { xs: 0.5, sm: 1, md: 1.5 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
      }}
    >
      <Grid2 size={{ xs: 12, md: 4 }}>
        <ListItem alignItems="center" dense sx={{ px: 0, py: 0.33 }}>
          <ListItemAvatar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              mr: 1,
            }}
          >
            <Icon
              name={icon}
              color={
                edit ? theme.palette.pink.main : theme.palette.primary.main
              }
              size={30}
            />
          </ListItemAvatar>
          <ListItemText
            primary={reportDetail?.folio || t("ReportViewer.no_folio")}
            slotProps={{
              primary: { variant: "h5", color: theme.palette.oxfordBlue.main },
              secondary: {
                variant: "body2",
                fontSize: 12.5,
                color: theme.palette.common.black,
              },
            }}
            secondary={
              reportDetail?.classification?.label ||
              t("ReportViewer.no_classification")
            }
          />
        </ListItem>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 4 }} mx="auto" px={1}>
        {edit ? (
          <Typography variant="h5" textAlign="center">
            {title}
          </Typography>
        ) : reportDetail.status.current === "closed" ? (
          <Typography fontSize={12} fontWeight={500} textAlign="center">
            {t("ReportsList.it_took")} {difference}{" "}
            {t("ReportsList.days_to_complete")}
          </Typography>
        ) : (
          <>
            <Typography fontSize={12} pl={0.5} fontWeight={500} textAlign="center">
              {reportDetail.open_days} {t("ReportsList.Open_days")} &gt;{" "}
              {reportDetail.expired_report} {t("ReportsList.days")}
            </Typography>
            <ProgressBar
              animated
              now={(reportDetail.open_days * 100) / reportDetail.expired_report}
              variant={
                reportDetail.open_days > reportDetail.expired_report
                  ? "danger"
                  : "info"
              }
            />
          </>
        )}
      </Grid2>

      <Grid2
        size="auto"
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 1,
          minWidth: 0,
          overflow: "hidden",
          pl: 1,
          ml: "auto",
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator=">"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            flexShrink: 0,
          }}
        >
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;

            const content = (
              <>
                {item.iconName && (
                  <Icon
                    name={item.iconName}
                    color={theme.palette.secondary.main}
                    size={13}
                  />
                )}
                <Box
                  component="span"
                  ml={item.iconName ? 0.5 : 0}
                  fontSize={11}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {item.label}
                </Box>
              </>
            );

            return isLast ? (
              <Typography
                key={index}
                fontSize={11}
                color="text.primary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                {content}
              </Typography>
            ) : (
              <Link
                key={index}
                underline="hover"
                color="inherit"
                href={item.route}
                onClick={(e) => handleClick(e, item.route)}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {content}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Grid2>
    </Grid2>
  );
};
